import React, { useEffect } from "react";
import Layout from "../../components/layout/layout";
import HeroBg from "../../assets/images/hero-image.png";
import Button from "../../components/library/button";
import { ExploreIcon } from "../../assets/images/svgExports";
import PlatformImage from "../../assets/images/platform-image.png";
import AfroCentricImage from "../../assets/images/africa_centric.png";
import { LandingPageAnimations } from "../../utils/animations/landing";
import gsap from "gsap";
import { useNavigate } from "react-router-dom";
import { FaBoltLightning } from "react-icons/fa6";
import { FiMinus, FiPlus } from "react-icons/fi";
import {
  africanMarketFeatures,
  cardData,
  ecosystemData,
  faqData,
  howItWorksSteps,
  platformFeatures,
} from "../../utils/dataHelpers/landingPageData";

const LandingPage: React.FC = () => {
  const navigate = useNavigate();

  useEffect(() => {
    let ctx = gsap.context(() => {
      LandingPageAnimations();
    });

    return () => ctx.revert();
  }, []);

  const Bolt = FaBoltLightning as React.ComponentType<{
    size?: string;
    color?: string;
  }>;
  const Plus = FiPlus as React.ComponentType<{
    size?: string;
  }>;
  const Minus = FiMinus as React.ComponentType<{
    color?: string;
  }>;

  const heroMetrics = [
    { value: "10+", label: "Enterprise Clients" },
    { value: "20M+", label: "Consumers Reached" },
    { value: "3+", label: "African Markets" },
    { value: "99.9%", label: "Platform Uptime" },
  ];

  const FAQItem: React.FC<{ question: string; answer: string }> = ({
    question,
    answer,
  }) => {
    const [isOpen, setIsOpen] = React.useState(false);

    return (
      <div className="faq-item border-b border-gray-200 py-4 cursor-pointer">
        <div
          className="flex justify-between items-center"
          onClick={() => setIsOpen(!isOpen)}
        >
          <h4
            className={`${
              isOpen ? "text-[#2C73DB]" : "#1E1E1E"
            } font-normal md:text-lg`}
          >
            {question}
          </h4>
          {isOpen ? <Minus color={"#2C73DB"} /> : <Plus />}
        </div>
        {isOpen && (
          <p className="mt-2 text-[#6F6C90] text-sm md:text-base pr-8">
            {answer}
          </p>
        )}
      </div>
    );
  };

  return (
    <Layout
      footerData={{
        headerText: "Engagement That Drives Real Growth",
        bodyText:
          "Bridgetune keeps your customers coming back through smarter, data-driven engagement. Take control of loyalty.",
      }}
    >
      <div className="text-gray-800 ">
        <section className="relative bg-cover bg-center md:px-12 px-4 flex justify-left items-end py-0 md:py-0 md:bg-center bg-right">
          <div
            className="hero-bg-wrapper absolute inset-0 w-full h-full bg-cover bg-right md:bg-center z-10 rounded-b-[28px] lg:rounded-b-[150px] w-full"
            style={{
              background: `linear-gradient(90deg, #000 -18.94%, rgba(0, 0, 0, 0.00) 88.81%), url(${HeroBg})`,
              backgroundRepeat: "no-repeat",
              backgroundPosition: "center",
              backgroundSize: "cover",
            }}
          />

          <div className="relative z-30 max-w-[600px] px-4  md:px-0 text-white flex flex-col justify-start h-full pt-[20vh] md:pb-32">
            <div className="hero-text mb-2">
              <div className="inline-flex items-center bg-[#2c73db33] text-[.6rem] font-medium py-2 px-3 rounded-full mb-3 border border-[#2C73DB] shadow-[0_0_18px_rgba(59,130,246,0.25)]">
                <Bolt color={"#2C73DB"} /> Licensed VAS Provider in Telecom
                Ecosystem
              </div>

              <h1 className="md:text-6xl text-4xl font-bold md:mb-6 mb-4 leading-tight w-full md:max-w-2xl font-[500]">
                Africa's Ecosystem <br />
                for Consumer- <br />
                Centric Growth
              </h1>
              <p className="md:text-[1rem] text-sm mb-2 max-w-[450px] font-light leading-[1.6rem]">
                We empower African companies with seamless technology and data
                solutions across every digital and mobile channel to grow
                revenue, drive engagement, and increase customer loyalty.
              </p>
            </div>

            <Button
              onClick={() => navigate("/solutions")}
              className="gap-2 w-[14rem] px-2 py-3 mb-20 md:mb-12 bg-[#2C73DB] hover:bg-[#2C73DB]/90 text-white rounded-[16px] font-light"
            >
              See What's Possible
              <ExploreIcon />
            </Button>
          </div>

          <div className="absolute  md:left-[5%] right-0 z-20 md:px-12 px-4 md:w-[90%] left-[0] metrics-section">
            <div className="bg-[#F0F6FF] rounded-xl flex justify-around p-4 md:p-8 mb-[-6rem] md:mb-[-4.5rem] overflow-x-auto border border-[#2C73DB] flex-wrap ">
              {heroMetrics.map((metric, idx) => (
                <div
                  key={idx}
                  className="text-center  w-[50%] md:w-[24%] md:mt-0 mt-2 metric-block"
                >
                  <h4 className="md:text-3xl text-2xl font-bold text-[#2C73DB] whitespace-nowrap">
                    {metric.value}
                  </h4>
                  <p className="md:text-sm text-[.6rem] text-[#1E1E1E] whitespace-nowrap">
                    {metric.label}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-white py-16 md:pt-32 pt-36 px-4 md:px-16 flex flex-col items-center about-section">
          <h2 className="section-title text-3xl md:text-4xl font-[100] text-gray-800 mb-2 section-title">
            Who We Are
          </h2>
          <p className="section-text split-body text-gray-500 mb-12 text-center text-sm md:text-base max-w-xl max-w-3xl leading-relaxed">
            We're a licensed Value-Added Services (VAS) provider transforming
            how African brands connect with their customers. Our mission is
            simple; help businesses engage deeply, and build lasting loyalty
          </p>

          <div className="flex flex-wrap justify-center gap-6">
            {cardData.map((card: any) => (
              <div
                key={card.title}
                className={`about-card w-full sm:w-80 md:w-64 lg:w-72 p-8 rounded-xl border-2 shadow-sm transition duration-300 flex flex-col items-center
            ${
              card.isBlue
                ? "bg-blue-600 border-blue-600 text-white"
                : "bg-white border-blue-200 text-gray-800 hover:shadow-lg"
            }`}
              >
                <div className="mb-6">{card.icon}</div>

                <h3
                  className={`text-2xl font-normal mb-3 ${
                    card.isBlue ? "text-white" : "text-gray-800"
                  }`}
                >
                  {card.title}
                </h3>

                <p
                  className={`text-center ${
                    card.isBlue ? "text-blue-100" : "text-gray-600"
                  }`}
                >
                  {card.description}
                </p>
              </div>
            ))}
          </div>
        </section>

        <section className="ecosystem-section p-12 py-16 px-0 md:px-16 flex justify-center">
          <div className="max-w-7xl bg-[#F0F6FF] rounded-[40px] lg:rounded-[60px] flex flex-col items-center py-10 md:py-20 px-4 md:px-8">
            <h2 className="section-title text-3xl md:text-4xl font-[100] text-gray-800 mb-3">
              Our Ecosystem
            </h2>
            <p className="section-text split-body text-[#1E1E1E] mb-3 text-center text-md md:text-base max-w-xl">
              A Collage Of Solutions
            </p>
            <p className="section-text split-body text-[#8E8E8E] text-sm max-w-[70%] md:max-w-2xl text-center capitalize mb-10">
              Bridgetune is not just a service provider—it's a complete
              ecosystem of possibilities. Choose what you need, integrate
              seamlessly, and watch your business transform.
            </p>

            <div className="w-full  space-y-12 overflow-hidden">
              {ecosystemData.map((section: any, idx: number) => (
                <div key={section.title}>
                  <div className={`flex  justify-center`}>
                    <div
                      className={`p-6 py-4 rounded-[20px] flex flex-col items-center bg-[#2C73DB]  text-white hover:shadow-md w-full md:w-[50%] text-center mb-6`}
                    >
                      <h3 className="ecosystem-header text-xl md:text-2xl font-[300] text-[#fff]  text-center">
                        {section.title}
                      </h3>
                    </div>
                  </div>

                  <div className="overflow-auto w-[92.5vw] md:w-full">
                    <div
                      className="flex gap-4 p-2 w-full"
                      style={{ width: "fit-content" }}
                    >
                      {section.blocks.map((block: any) => {
                        return (
                          <div
                            key={block.title}
                            className={`transition duration-300 ecosystem-card p-6 px-6 md:px-10 rounded-[20px]  flex flex-col items-center transition duration-300 bg-white text-center w-[25%] min-w-[280px] border-2 hover:border-white hover:border-blue-200`}
                          >
                            <div className="mb-4">
                              <div className="w-8 h-8 rounded-full flex items-center justify-center">
                                {block.icon}
                              </div>
                            </div>
                            <h3 className="text-lg md:text-xl font-semibold mb-2 text-[#1E1E1E]">
                              {block.title}
                            </h3>
                            <p className="text-xs md:text-sm max-w-sm text-[#8E8E8E]">
                              {block.description}
                            </p>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="platform-section bg-white px-4 md:px-16 flex flex-col items-center">
          <h2 className="section-title text-3xl md:text-4xl font-[100] text-gray-800 mb-2">
            Our Platform
          </h2>
          <p className="section-text split-body text-[#8e8e8e] mb-12 text-center text-sm md:text-base max-w-2xl">
            The Bridge is our B2B tech architecture designed to drive revenue
            and increase retention through seamless integration and intelligent
            automation.
          </p>

          <div className="flex flex-col md:flex-row max-w-7xl w-full gap-8 items-center">
            <div className="md:w-1/2 space-y-6">
              {platformFeatures.map((feature, idx) => (
                <div key={idx} className="platform-item flex items-start gap-4">
                  <div className="text-[#2C73DB] mt-1">{feature.icon}</div>
                  <p className="text-gray-700 text-base">{feature.text}</p>
                </div>
              ))}
              <Button
                onClick={() => navigate("/solutions")}
                className="gap-2 w-[14rem] px-2 py-3 mb-8 md:mb-12 bg-[#2C73DB] hover:bg-[#2C73DB]/90 text-white rounded-[16px] font-light"
              >
                Learn More <ExploreIcon />
              </Button>
            </div>
            <div className="md:w-1/2">
              <img
                src={PlatformImage}
                alt="Bridgetune Platform Dashboard"
                className="platform-image image-animate rounded-xl shadow-2xl w-full h-auto object-cover"
              />
            </div>
          </div>
        </section>

        <section className="how-section mt-16">
          <div className="w-full lg:w-[85%] bg-[#2C73DB]  mx-auto flex flex-col items-center px-4 md:px-8 p-8 py-8 md:py-16 rounded-[40px] lg:rounded-[60px]">
            <h2 className="section-title text-3xl md:text-4xl font-[100] text-[#fff] mb-2">
              How It Works
            </h2>
            <p className="section-text split-body text-[#fff] mb-12 text-center text-sm md:text-base max-w-2xl">
              Get started in four simple steps. Our plug-in platform makes
              integration effortless, so you can focus on what matters—growing
              your business.
            </p>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 max-w-7xl">
              {howItWorksSteps.map((step) => (
                <div
                  key={step.step}
                  className="how-card relative bg-white p-6 rounded-[20px] flex flex-col items-center text-center shadow-lg hover:shadow-xl transition duration-300"
                >
                  <div className="px-6 flex items-center justify-center rounded-[20px] bg-[#2C73DB] absolute top-[-20px] z-30">
                    <span className="text-2xl font-bold text-[#fff] ">
                      {step.step}
                    </span>
                  </div>
                  <div>{step.icon}</div>

                  <h3 className="text-xl font-semibold text-[#1e1e1e] my-2">
                    {step.title}
                  </h3>
                  <p className="text-sm text-[#8e8e8e]">{step.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section>
          <div className="africa-section w-full lg:w-[85%] bg-[#F0F6FF]  mx-auto flex flex-col lg:flex-row gap-10 px-4 md:px-8 p-8 py-8 md:py-16 rounded-[40px] lg:rounded-[60px] mb-4 mt-20">
            <div className="lg:w-1/2 order-2 lg:order-1">
              <div className="inline-flex items-center bg-[#2c73db33] text-[.6rem] font-medium py-2 px-3 rounded-full mb-3 border border-[#2C73DB] shadow-[0_0_18px_rgba(59,130,246,0.25)]">
                Why Africa?
              </div>
              <h2 className="section-title text-3xl md:text-3xl font-[100] text-gray-800 mb-4">
                Rooted in African Markets
              </h2>
              <p className="section-text split-body text-[#8e8e8e] mb-8 text-md">
                Africa isn't just our market—it's our home. Bridgetune was built
                from the ground up to serve African consumers with technology
                and data solutions that understand local context, culture, and
                market dynamics.
              </p>
              <div className="space-y-4">
                {africanMarketFeatures.map((feature, idx) => (
                  <div
                    key={idx}
                    className="africa-feature flex items-center gap-4 bg-white rounded-[20px] p-4"
                  >
                    <div>{feature.icon}</div>
                    <div>
                      <h4 className="font-[100] text-[#1e1e1e]">
                        {feature.title}
                      </h4>
                      <p className="text-xs text-[#8e8e8e]">
                        {feature.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
              <div className=" bg-[#2c73db33]  text-sm font-medium p-4 rounded-[20px] mt-4 border border-[#2C73DB]">
                <span className="text-[#2C73DB]">
                  3+ African markets. 10+ enterprise clients·
                </span>{" "}
                20M+ consumers reached·{" "}
                <span className="text-[#2C73DB]">99.9% uptime </span>
              </div>
            </div>
            <div className="lg:w-1/2 order-1 lg:order-2 h-auto rounded-[20px] overflow-hidden relative">
              <img
                src={AfroCentricImage}
                alt="African team collaboration"
                className="image-animate w-full h-full xl:max-h-[645px] object-cover shadow-xl"
              />
              <div className="absolute w-full bottom-6">
                <div className="w-[90%] mx-auto bg-[#2c73db6d]  text-sm font-medium p-4 rounded-[20px] mt-4 border border-[#2C73DB]">
                  <h2 className="section-title text-xl font-[100] text-[#fff] mb-2">
                    African-Centric Design
                  </h2>
                  <p className="text-sm text-[#fff]">
                    Every solution is rooted in African context, built with
                    local insights and market expertise.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="faq-section py-16">
          <div className="w-full lg:w-[85%]  mx-auto py-16 px-4 md:px-8   bg-[#F0F6FF] rounded-[40px] lg:rounded-[60px] flex flex-col items-center">
            <h2 className="section-title text-3xl md:text-4xl font-bold text-gray-800 mb-8 text-center">
              Frequently Asked Questions
            </h2>
            <span className="section-text split-body text-[#8E8E8E] max-w-2xl text-center">
              Got questions? We've got answers. Find everything you need to know
              about our platform, pricing, and features.
            </span>

            <div className="pt-4 w-full max-w-5xl">
              {faqData.map((item: any, index: any) => (
                <>
                  <FAQItem
                    key={index}
                    question={item.question}
                    answer={item.answer}
                  />
                </>
              ))}
            </div>

            <div className="mt-10 flex flex-col items-center w-full">
              <p className="text-[#1E1E1E] mb-4">
                Still have questions? We're here to help.
              </p>
              <Button
                onClick={() => navigate("/contact")}
                className="gap-2 px-8 py-3 bg-[#2C73DB] hover:bg-[#2C73DB]/90 text-white rounded-[20px] font-light w-[14rem]"
              >
                Contact Our Team
              </Button>
            </div>
          </div>
        </section>
      </div>
    </Layout>
  );
};

export default LandingPage;
